import { createFileRoute, notFound } from '@tanstack/react-router'
import ProjectDetailsTab from '../../Pages/ProjectPage/ProjectDetailsTab'
import ProjectPageHeader from '../../Pages/ProjectPage/ProjectPageHeader'
import PageBody from '../../Pages/Layout/PageBody'
import LoadingSpinner from '../../Components/LoadingSpinner'
import ErrorComponent from '../../Components/ErrorComponent'
import {
    canViewRoute,
    migratedToV2,
} from '../../State/Permissions/HasPermissions'
import SessionStore from '../../State/SessionStore'
import PermissionDenied from '../../Pages/PermissionDenied'
import ProjectForecastsTab from '../../Pages/ProjectPage/forecasts/ProjectForecastsTab'
import ProjectForecastsSidebar from '../../Pages/ProjectPage/forecasts/ProjectForecastsSidebar'
import ProjectForecastHeader from '../../Pages/ProjectPage/forecasts/ProjectForecastHeader'
import ProjectTasksTab from '../../Pages/ProjectPage/ProjectTasksTab'
import ProjectChangeLogTab from '../../Pages/ProjectPage/ProjectChangeLogTab'
import ProjectRatesTab from '../../Pages/ProjectPage/ProjectRatesTab'
import ProjectNotesTab from '../../Pages/ProjectPage/ProjectNotesTab'
import ProjectStatusTab from '../../Pages/ProjectPage/ProjectStatusTab'
import ProjectMilestonesTab from '../../Pages/ProjectPage/ProjectMilestonesTab'
import PageSidebar from '../../Pages/Layout/PageSidebar'
import ProjectHistoryTab from '../../Pages/ProjectPage/ProjectHistoryTab'
import DataStore from '../../State/DataStore'
import { router } from '../../App'
import ProjectCollection from '../../State/Collections/ProjectCollection'
import { setLayoutDefaults } from '@2/layout/layout-store'
import { getCacheItemById } from '@2/cache'
import { CenterPadPageBody } from '@2/layout/page-body'
import { trpc } from '../../system/trpc'
import { processTRPCData } from '../../Queries/fetchData'
import { cleanupNewEntity } from '../../Utils/deleteEntity'
import { projectActions } from '../../version2/pages/project-page/project-page-store'

const tabs = {
    details: {
        label: 'Project Details',
        id: 'details',
        page: (props) => <ProjectDetailsTab {...props} />,
        collections: (id) => {
            return []
        },
        permissions: (params) =>
            canViewRoute(SessionStore.user, 'project-details', params),
    },
    forecasts: {
        label: 'Forecasts',
        id: 'forecasts',
        page: (props) => <ProjectForecastsTab {...props} />,
        header: (props) => <ProjectForecastHeader {...props} />,
        sidebar: (props) => <ProjectForecastsSidebar {...props} />,
        collections: () => {
            return []
        },
        permissions: (params) =>
            canViewRoute(SessionStore.user, 'project-forecasts', params),
    },
    milestones: {
        label: 'Milestones',
        id: 'milestones',
        page: (props) => <ProjectMilestonesTab {...props} />,
        collections: (id) => {
            return []
        },
        permissions: (params) =>
            canViewRoute(SessionStore.user, 'project-milestones', params),
    },
    tasks: {
        label: 'Tasks',
        id: 'tasks',
        page: (props) => <ProjectTasksTab {...props} />,
        collections: (id) => {
            return []
        },
        permissions: (params) =>
            canViewRoute(SessionStore.user, 'project-tasks', params),
    },
    changelog: {
        label: 'Changelog',
        id: 'changelog',
        page: (props) => <ProjectChangeLogTab {...props} />,
        collections: (id) => {
            return []
        },
        permissions: (params) =>
            canViewRoute(SessionStore.user, 'project-changelog', params),
    },
    rates: {
        label: 'Rates',
        id: 'rates',
        page: (props) => <ProjectRatesTab {...props} />,
        collections: (id) => {
            return []
        },
        permissions: (params) =>
            canViewRoute(SessionStore.user, 'project-rates', params),
    },
    notes: {
        label: 'Notes',
        id: 'notes',
        page: (props) => <ProjectNotesTab {...props} />,
        collections: (id) => {
            return []
        },
        permissions: (params) =>
            canViewRoute(SessionStore.user, 'project-notes', params),
    },
    history: {
        label: 'History',
        id: 'history',
        page: (props) => <ProjectHistoryTab {...props} />,
        collections: (id) => {
            return []
        },
        permissions: (params) =>
            canViewRoute(SessionStore.user, 'project-history', params),
    },
    status: {
        label: 'Status',
        id: 'status',
        page: (props) => <ProjectStatusTab {...props} />,
        collections: (id) => {
            return []
        },
        permissions: (params) =>
            canViewRoute(SessionStore.user, 'project-status', params),
    },
}

export const Route = createFileRoute('/_private/projects/$id')({
    beforeLoad: ({ params, search }) => {
        const activeTab = search?.tab || 'details'
        if (
            !canViewRoute(SessionStore.user, 'project', params) ||
            !canViewRoute(SessionStore.user, `project-${activeTab}`, params)
        )
            throw new Error('Permission Denied')
        const project = getCacheItemById('projects', params.id)
        setLayoutDefaults({
            pageTitle: project?.name || 'Project',
            subMenu: 'projects',
            tabs: Object.values(tabs).map((t) => ({
                id: t.id,
                label: t.label,
            })),
        })
    },
    errorComponent: ({ error, reset }) => {
        if (error.message === 'Permission Denied') return <PermissionDenied />
        return <ErrorComponent error={error} reset={reset} />
    },
    loaderDeps: ({ search: { tab } }) => ({
        tab,
    }),
    loader: async ({ params, location }) =>
        await loader(params.id, location.search),
    pendingComponent: LoadingSpinner,
    component: Wrapper,
    onLeave: (match) => {
        const { id } = match.params
        const project = ProjectCollection.projectsById[id]
        if (
            project &&
            !project.createdAt &&
            match.pathname !== router.latestLocation.pathname
        ) {
            cleanupNewEntity(project, 'project')
        }
    },
})

const loader = async (id, search) => {
    const { tab } = search
    const activeTab = tab || 'details'
    const data = await trpc.legacy.getProjectPageData.query({ id })
    const processedModels = processTRPCData(data)

    const project = ProjectCollection.projectsById[id]
    if (!project) {
        throw notFound()
    }
    return processedModels
}

function Wrapper() {
    const { tab } = Route.useSearch()
    const params = Route.useParams()
    const activeTab = tab || 'details'
    if (migratedToV2()) {
        return (
            <CenterPadPageBody>
                {tabs[activeTab].page(params)}
            </CenterPadPageBody>
        )
    }

    return (
        <>
            {tabs[activeTab].header ? (
                tabs[activeTab].header({
                    tabs: Object.values(tabs).filter((t) =>
                        t.permissions(params)
                    ),
                    selectedTab: activeTab,
                    ...params,
                })
            ) : (
                <ProjectPageHeader
                    tabs={Object.values(tabs).filter((t) =>
                        t.permissions(params)
                    )}
                    selectedTab={activeTab}
                    {...params}
                />
            )}
            <PageBody>{tabs[activeTab].page(params)}</PageBody>

            {tabs[activeTab].sidebar && (
                <PageSidebar>
                    {tabs[activeTab].sidebar({
                        ...params,
                    })}
                </PageSidebar>
            )}
        </>
    )
}
